// JS 모듈 URL 에서 API base 를 자동 감지 (서브패스 배포 대응 + Vite dev 호환).
//   prod 빌드: https://example.com/assets/index-xxx.js          → /api
//             https://example.com/semantic/assets/index-xxx.js  → /semantic/api
//   Vite dev: http://localhost:8002/src/api.js?t=...             → /api
//             (vite proxy 가 /api/* 를 백엔드 8003 으로 전달)
//
// `/assets/` 가 있으면 그 앞을 prefix 로, 없으면 빈 prefix (= /api 직접) 사용.
const _path = new URL(import.meta.url).pathname
const _prefix = _path.includes('/assets/')
  ? _path.replace(/\/assets\/.*$/, '')
  : ''
const BASE = _prefix + '/api'

// ── 토큰 관리 ────────────────────────────────────────────────────────────────
const T = 'svs_token', R = 'svs_refresh', U = 'svs_user'
export const getToken        = () => localStorage.getItem(T)
export const getRefreshToken = () => localStorage.getItem(R)
export const getStoredUser   = () => { try { return JSON.parse(localStorage.getItem(U)) } catch { return null } }
export const setTokens = (tok, ref, user) => {
  if (tok)  localStorage.setItem(T, tok)
  if (ref)  localStorage.setItem(R, ref)
  if (user) localStorage.setItem(U, JSON.stringify(user))
}
export const clearTokens = () => { localStorage.removeItem(T); localStorage.removeItem(R); localStorage.removeItem(U) }

let _onUnauthorized = null
export const setOnUnauthorized = (fn) => { _onUnauthorized = fn }

// ── HTTP 요청 ────────────────────────────────────────────────────────────────
// 응답을 JSON으로 파싱하되, 본문이 비었거나 JSON이 아니면 빈 객체로 폴백.
async function _parseJsonOr(res) {
  return res.json().catch(() => ({}))
}

// 비-OK 응답을 사용자 친화적 메시지로 throw.
async function _throwHttpError(res) {
  const err = await res.json().catch(() => ({ detail: res.statusText }))
  const detail = err.detail || err.error
  const msg = typeof detail === 'string' ? detail
    : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
    : JSON.stringify(detail) || res.statusText
  throw new Error(msg)
}

async function request(method, path, body, { signal, skipAuth } = {}) {
  const buildHeaders = () => {
    const h = { 'Content-Type': 'application/json' }
    const t = getToken()
    if (t && !skipAuth) h['Authorization'] = `Bearer ${t}`
    return h
  }
  const doFetch = (h) => fetch(BASE + path, {
    method, headers: h,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  })

  let res = await doFetch(buildHeaders())

  // 401 → refresh 1회 → 재시도. 재시도가 또 401이면 그때만 로그아웃.
  // 그 외 status(500/400/...)는 일반 에러로 throw — 로그아웃하지 않는다.
  if (res.status === 401 && !skipAuth && !path.startsWith('/auth/')) {
    const ok = await _tryRefresh()
    if (ok) {
      res = await doFetch(buildHeaders())
      if (res.ok) return res.json()
      if (res.status !== 401) return _throwHttpError(res)
    }
    // refresh 실패했거나 retry도 401 → 진짜 인증 만료
    if (_onUnauthorized) _onUnauthorized()
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.')
  }

  if (!res.ok) return _throwHttpError(res)
  return res.json()
}

/** JSON이 아닌 text 응답을 받는 요청 */
async function requestText(method, path) {
  const buildHeaders = () => {
    const h = {}
    const t = getToken()
    if (t) h['Authorization'] = `Bearer ${t}`
    return h
  }
  const doFetch = (h) => fetch(BASE + path, { method, headers: h })

  let res = await doFetch(buildHeaders())

  if (res.status === 401 && !path.startsWith('/auth/')) {
    const ok = await _tryRefresh()
    if (ok) {
      res = await doFetch(buildHeaders())
      if (res.ok) return res.text()
      if (res.status !== 401) {
        const err = await _parseJsonOr(res)
        throw new Error(err.detail || res.statusText)
      }
    }
    if (_onUnauthorized) _onUnauthorized()
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.')
  }

  if (!res.ok) {
    const err = await _parseJsonOr(res)
    throw new Error(err.detail || res.statusText)
  }
  return res.text()
}

// in-flight refresh promise — 동시 호출들이 같은 promise를 공유해
// 같은 refresh_token 으로 N번 /auth/refresh 를 두드리지 않도록 한다.
let _refreshInFlight = null

async function _tryRefresh() {
  if (_refreshInFlight) return _refreshInFlight
  _refreshInFlight = (async () => {
    const rt = getRefreshToken()
    if (!rt) return false
    try {
      const res = await fetch(BASE + '/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      })
      if (!res.ok) { clearTokens(); return false }
      const d = await res.json()
      setTokens(d.token, null, d.user)
      return true
    } catch { clearTokens(); return false }
  })()
  try {
    return await _refreshInFlight
  } finally {
    _refreshInFlight = null
  }
}

/** settings → 스토리지 query string (user_id 포함) */
function storageQs(s) {
  const uid = s?.user_id ? `&user_id=${enc(s.user_id)}` : ''
  if (!s) return 'storage_type=firestore'
  if (s.storage_type === 'firestore' && s.fs_project && s.fs_database && s.fs_collection) {
    return `storage_type=firestore&fs_project=${enc(s.fs_project)}&fs_database=${enc(s.fs_database)}&fs_collection=${enc(s.fs_collection)}${uid}`
  }
  if (s.storage_type === 'gcs' && s.gcs_path) {
    return `storage_type=gcs&gcs_path=${enc(s.gcs_path)}${uid}`
  }
  return `storage_type=firestore${uid}`
}

function enc(v) { return encodeURIComponent(v) }

export const api = {
  // ── 인증 ──────────────────────────────────────────────────────────────────
  login:          (uid, pw) => request('POST', '/auth/login', { user_id: uid, password: pw }, { skipAuth: true }),
  refreshToken:   (rt)      => request('POST', '/auth/refresh', { refresh_token: rt }, { skipAuth: true }),
  getMe:          ()        => request('GET',  '/auth/me'),
  changePassword: (uid, cur, nw) => request('POST', '/auth/change_password', { user_id: uid, current_password: cur, new_password: nw }, { skipAuth: true }),

  /** 서버 환경변수 기반 설정 기본값 조회 */
  getDefaults: ()          => request('GET', '/defaults'),

  // ── 사용자 / 공유 기본 설정 (Firestore semantic_view_settings) ────────────
  getUserSettings:     ()  => request('GET', '/user/settings'),
  saveUserSettings:    (s) => request('PUT', '/user/settings', { settings: s }),
  getDefaultSettings:  ()  => request('GET', '/admin/settings/default'),
  saveDefaultSettings: (s) => request('PUT', '/admin/settings/default', { settings: s }),

  // ── 인스트럭션 편집 (사용자별 + admin 공유 기본값) ───────────────────────
  listInstructions:        ()              => request('GET',    '/instructions'),
  getInstruction:          (name)          => request('GET',    `/instructions/${enc(name)}`),
  saveInstruction:         (name, content) => request('PUT',    `/instructions/${enc(name)}`, { content }),
  deleteInstruction:       (name)          => request('DELETE', `/instructions/${enc(name)}`),
  // KBNL-131: 기본값 변경 알림 — 공유 기본값 내용 조회 / '내 설정 유지'(알림 끄기)
  getInstructionShared:    (name)          => request('GET',    `/instructions/${enc(name)}/shared`),
  ackInstructionDefaultChange: (name)      => request('POST',   `/instructions/${enc(name)}/ack-default-change`),
  getInstructionDefault:   (name)          => request('GET',    `/instructions/${enc(name)}/default`),
  getAdminInstruction:     (name)          => request('GET',    `/admin/instructions/${enc(name)}`),
  saveAdminInstruction:    (name, content) => request('PUT',    `/admin/instructions/${enc(name)}`, { content }),
  // KBNL-136: 인스트럭션 수정 이력 (버전 관리). user=내 override / admin=공유 기본값.
  // 모델 사용 가능 체크 — {kind:'embedding'|'gemini', model, location?} → {ok, dim, latency_ms, error}
  // location: 리전별 가용성이 달라 실호출과 같은 리전으로 검증 (비면 서버 env 기본)
  modelCheck: (kind, model, location = '') => request('POST', '/settings/model-check', { kind, model, location }),
  // 임베딩 누락 status / 백필 (admin)
  embeddingsStatus:   () => request('GET',  '/admin/embeddings/status'),
  embeddingsBackfill: () => request('POST', '/admin/embeddings/backfill'),
  // 전체 재색인 (admin) — Firestore 분산 리스로 다중 인스턴스 중복 실행 방지,
  // 백그라운드 실행 후 status 폴링으로 진행률 표시
  embeddingsReindex:       () => request('POST', '/admin/embeddings/reindex'),
  embeddingsReindexStatus: () => request('GET',  '/admin/embeddings/reindex/status'),
  getInstructionHistory:        (name)      => request('GET',    `/instructions/${enc(name)}/history`),
  getInstructionHistoryEntry:   (name, id)  => request('GET',    `/instructions/${enc(name)}/history/${enc(id)}`),
  deleteInstructionHistoryEntry: (name, id) => request('DELETE', `/instructions/${enc(name)}/history/${enc(id)}`),
  getAdminInstructionHistory:      (name)   => request('GET',    `/admin/instructions/${enc(name)}/history`),
  getAdminInstructionHistoryEntry: (name, id) => request('GET',  `/admin/instructions/${enc(name)}/history/${enc(id)}`),
  deleteAdminInstructionHistoryEntry: (name, id) => request('DELETE', `/admin/instructions/${enc(name)}/history/${enc(id)}`),

  generate: (data, opts)   => request('POST', '/generate', data, opts),
  convert:  (data, opts)   => request('POST', '/convert', data, opts),
  convertSemantic: (data, opts) => request('POST', '/convert-semantic', data, opts),

  // ── 뷰 CRUD (로컬 / GCS / Firestore) ──────────────────────────────────
  listViews: (s) =>
    request('GET', `/views?${storageQs(s)}`),

  getView: (name, s, ownerUserId = '') => {
    const qs = storageQs(s) + (ownerUserId ? `&owner_user_id=${enc(ownerUserId)}` : '')
    return request('GET', `/views/${enc(name)}?${qs}`)
  },

  saveView: (data, s) =>
    request('POST', `/views?${storageQs(s)}`, data),

  deleteView: (name, s) =>
    request('DELETE', `/views/${enc(name)}?${storageQs(s)}`),

  renameView: (name, newName, s) =>
    request('PUT', `/views/${enc(name)}/rename?${storageQs(s)}`, { new_name: newName }),

  // ── 탐색 (다른 사용자의 뷰 탐색) ────────────────────────────────────────
  listExploreUsers: (s) =>
    request('GET', `/explore/users?${storageQs({ ...(s || {}), user_id: '' })}`),

  // 모든 사용자 × type(semantic|composite)의 뷰 목록을 한 번에 가져온다.
  // Explore.jsx 의 사용자별 N개 호출 (HTTP/Firestore RPC 모두 N배) 을
  // 단일 호출로 대체하기 위한 집계 엔드포인트.
  listExploreAllViews: (s, type) =>
    request('GET', `/explore/all-views?type=${enc(type)}&${storageQs({ ...(s || {}), user_id: '' })}`),

  // ── 이관 ─────────────────────────────────────────────────────────────────
  migrate: (body) =>
    request('POST', '/views/migrate', body),

  // ── 메타데이터 ──────────────────────────────────────────────────────────
  listDatasets: (projectId, location) => request('GET', `/datasets?project_id=${enc(projectId)}&location=${enc(location || '')}`),
  listTables: (projectId, datasetId) => request('GET', `/tables?project_id=${enc(projectId)}&dataset_id=${enc(datasetId)}`),
  listGlossaries: (projectId, location) => request('GET', `/glossaries?project_id=${enc(projectId)}&location=${enc(location || '')}`),

  // ── Firestore 메타 ─────────────────────────────────────────────────────
  listFirestoreDatabases: (projectId) => request('GET', `/firestore/databases?project_id=${enc(projectId)}`),
  listFirestoreCollections: (projectId, database) => request('GET', `/firestore/collections?project_id=${enc(projectId)}&database=${enc(database)}`),

  // ── Composite Semantic View ───────────────────────────────────────────
  compositeGenerate:  (data, opts) => request('POST', '/composite/generate', data, opts),
  // 리뷰 #2: storageSettings 두 번째 인자 — backend (local/GCS/non-default Firestore)
  // 에서도 missing ref auto-resolve 가 동일 저장소를 보도록 query 로 전달.
  // 기존 호출 (storageSettings 미지정) 도 동작 — 백엔드가 기본 firestore 로 fallback.
  compositeAssemble:  (data, storageSettings) =>
    request('POST', `/composite/assemble?${storageQs(storageSettings)}`, data),
  compositeValidate:  (data, opts) => request('POST', '/composite/validate', data, opts),
  compositeAiCombine: (data, opts) => request('POST', '/composite/ai-combine', data, opts),
  compositeSample:    ()           => requestText('GET', '/composite/sample'),

  // ── GCS 폴더 탐색 + Export (KBNL-27) ──────────────────────────────────
  gcsList:   (path)        => request('GET',  `/gcs/list?path=${encodeURIComponent(path)}`),
  gcsExport: (data)        => request('POST', `/export/gcs`, data),
  // KBNL-99: GCS 단일 파일 import — { path } → { path, content, size, updated }
  gcsImport: (path)        => request('POST', `/import/gcs/sv`, { path }),

  // ── Composite View 저장/로드 ────────────────────────────────────────
  listCompositeViews: (s) =>
    request('GET', `/composite/views?${storageQs(s)}`),

  // 리뷰 #1: ownerUserId 가 주어지면 협업 모드 cross-owner 정확 조회
  // (같은 이름의 CV 가 여러 owner 에 있을 때 의도된 owner 의 정의를 가져옴).
  // 백엔드 get_composite_view 는 owner_user_id query 를 이미 지원.
  getCompositeView: (name, s, ownerUserId = '') => {
    const qs = storageQs(s) + (ownerUserId ? `&owner_user_id=${enc(ownerUserId)}` : '')
    return request('GET', `/composite/views/${enc(name)}?${qs}`)
  },

  saveCompositeView: (data, s) =>
    request('POST', `/composite/views?${storageQs(s)}`, data),

  deleteCompositeView: (name, s) =>
    request('DELETE', `/composite/views/${enc(name)}?${storageQs(s)}`),

  renameCompositeView: (name, newName, s) =>
    request('PUT', `/composite/views/${enc(name)}/rename?${storageQs(s)}`, { new_name: newName }),

  // ── SQL Query CRUD ──────────────────────────────────────────────────
  listSqlQueries: (s) =>
    request('GET', `/sql-queries?${storageQs(s)}`),

  // ownerUserId 가 주어지면 협업 모드 cross-owner 정확 조회 (같은 이름이 여러 owner 에 있을 때).
  getSqlQuery: (name, s, ownerUserId = '') => {
    const qs = storageQs(s) + (ownerUserId ? `&owner_user_id=${enc(ownerUserId)}` : '')
    return request('GET', `/sql-queries/${enc(name)}?${qs}`)
  },

  saveSqlQuery: (data, s) =>
    request('POST', `/sql-queries?${storageQs(s)}`, data),

  // ── Workbench 변환 실행 presence/리스 (KBNL-106) ────────────────────
  // 같은 워크벤치 동시 변환 차단 + 타 창 '변환 중' 스피너 표시용.
  wbRunLeaseAcquire:   (body) => request('POST', '/workbench/run-lease/acquire', body),  wbRunLeaseHeartbeat: (body) => request('POST', '/workbench/run-lease/heartbeat', body),
  wbRunLeaseRelease:   (body) => request('POST', '/workbench/run-lease/release', body),
  wbRunLeaseStatus:    (name, ownerUserId = '') =>
    request('GET', `/workbench/run-lease/status?name=${enc(name)}&owner_user_id=${enc(ownerUserId)}`),

  // ── Workbench 변환 잡(job) 미러 (KBNL-126) ──────────────────────────
  // 새로고침/브라우저 종료/중단에도 변환 결과 보존·회수.
  wbJobsList:    (name, ownerUserId = '') =>
    request('GET', `/workbench/jobs?name=${enc(name)}&owner_user_id=${enc(ownerUserId)}`),
  wbJobsRunning: () => request('GET', '/workbench/jobs/running'),  // KBNL-141: 목록 스피너용
  wbJobsCollect: (jobId) => request('POST', '/workbench/jobs/collect', { job_id: jobId }),
  wbJobsCancel:  (jobId) => request('POST', '/workbench/jobs/cancel', { job_id: jobId }),  // KBNL-141: 강제 중단

  deleteSqlQuery: (name, s) =>
    request('DELETE', `/sql-queries/${enc(name)}?${storageQs(s)}`),

  renameSqlQuery: (name, newName, s) =>
    request('PUT', `/sql-queries/${enc(name)}/rename?${storageQs(s)}`, { new_name: newName }),

  // ── SQL → Semantic View 변환 ──────────────────────────────────────
  sqlToSemantic: (data, opts) => request('POST', '/sql-to-semantic', data, opts),

  // ── 기존 SV 추천 (하이브리드 서치, KBNL-74) ──────────────────────
  recommendReferenceViews: (data, opts) => request('POST', '/sql-to-semantic/recommend-views', data, opts),

  // ── SV 자연어 키워드 추출 (KBNL-74 후속, prefill 용) ──────────────
  // backend 의 extract_embedding_text() 와 동일한 결과 반환 — 검색바
  // prefill 과 vector 채널 입력 텍스트가 일치하도록.
  extractSvKeywords: (data, opts) => request('POST', '/sv/extracted-keywords', data, opts),

  // ── SQL Workbench 중간 단계 정제 (SQL → SQL) ─────────────────────
  sqlWorkbenchRefine: (data, opts) => request('POST', '/sql-workbench/refine', data, opts),

  // ── SQL Workbench YAML 정제 (YAML → YAML, KBNL-66) ───────────────
  sqlWorkbenchRefineYaml: (data, opts) => request('POST', '/sql-workbench/refine-yaml', data, opts),

  // KBNL-120: yaml_to_sql 의 LLM 엔진 — YAML → BigQuery SQL (1회 LLM 호출)
  sqlWorkbenchYamlToSql: (data, opts) => request('POST', '/sql-workbench/yaml-to-sql', data, opts),

  // ── KBNL-101: 존재하지 않는 테이블/컬럼 검증 ─────────────────────
  validateSql:      (data, opts) => request('POST', '/validate/sql', data, opts),
  validateSemantic: (data, opts) => request('POST', '/validate/semantic', data, opts),

  // ── Virtual Schema (KBNL-22: Firestore 백엔드, doc_id = '{owner_user_id}__{name}') ──
  listVirtualSchemas:     ()                  => request('GET',    '/virtual-schemas'),
  getVirtualSchema:       (docId)             => request('GET',    `/virtual-schemas/${enc(docId)}`),
  createVirtualSchema:    (body)              => request('POST',   '/virtual-schemas', body),
  updateVirtualSchema:    (docId, body)       => request('PUT',    `/virtual-schemas/${enc(docId)}`, body),
  deleteVirtualSchema:    (docId)             => request('DELETE', `/virtual-schemas/${enc(docId)}`),
  setVirtualSchemaActive: (docId, active)     => request('PUT',    `/virtual-schemas/${enc(docId)}/active`, { active_version: active }),
  renameVirtualSchema:    (docId, newName)    => request('PUT',    `/virtual-schemas/${enc(docId)}/rename`, { new_name: newName }),
  summarizeVirtualSchema: (docId, body, opts) => request('POST',   `/virtual-schemas/${enc(docId)}/summarize`, body || {}, opts),

  // ── Sandbox 챗봇 ───────────────────────────────────────
  getSandboxLimits:      ()                  => request('GET',  '/sandbox/limits'),
  listSandboxSessions:   ()                  => request('GET',  '/sandbox/sessions'),
  createSandboxSession:  (body)              => request('POST', '/sandbox/sessions', body || {}),
  getSandboxSession:     (sid)               => request('GET',  `/sandbox/sessions/${enc(sid)}`),
  renameSandboxSession:  (sid, newTitle)     => request('PUT',  `/sandbox/sessions/${enc(sid)}/rename`, { new_title: newTitle }),
  deleteSandboxSession:  (sid)               => request('DELETE', `/sandbox/sessions/${enc(sid)}`),
  deleteAllSandboxSessions: ()               => request('DELETE', '/sandbox/sessions'),
  clearSandboxSessionMessages: (sid)         => request('DELETE', `/sandbox/sessions/${enc(sid)}/messages`),
  // KBNL-67: 주어진 메시지부터 끝까지 일괄 삭제 (truncate)
  truncateSandboxMessages: (sid, mid)        => request('DELETE', `/sandbox/sessions/${enc(sid)}/messages/from/${enc(mid)}`),
  sendSandboxMessage:    (sid, body, opts)   => request('POST', `/sandbox/sessions/${enc(sid)}/messages`, body, opts),
  // KBNL-127: 중단/에러 난 assistant 메시지 재생성
  retrySandboxMessage:   (sid, mid, body, opts) => request('POST', `/sandbox/sessions/${enc(sid)}/messages/${enc(mid)}/retry`, body, opts),

  /** 첨부 파일 업로드 (multipart/form-data — 일반 request() 대신 raw fetch). */
  uploadSandboxFile: async (sid, file) => {
    const fd = new FormData()
    fd.append('file', file)
    const url = BASE + `/sandbox/sessions/${enc(sid)}/upload`
    const headers = {}
    const t = getToken()
    if (t) headers['Authorization'] = `Bearer ${t}`
    let res = await fetch(url, { method: 'POST', headers, body: fd })
    if (res.status === 401) {
      const ok = await _tryRefresh()
      if (ok) {
        const t2 = getToken()
        if (t2) headers['Authorization'] = `Bearer ${t2}`
        res = await fetch(url, { method: 'POST', headers, body: fd })
      }
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }))
      throw new Error(err.detail || res.statusText)
    }
    return res.json()
  },

  /** 다운로드 URL — `<img src>` / `<a download>` 직접 사용 (token query param). */
  sandboxFileUrl: (sid, fid) => {
    const t = getToken() || ''
    const q = t ? `?token=${enc(t)}` : ''
    return BASE + `/sandbox/sessions/${enc(sid)}/files/${enc(fid)}${q}`
  },
}
